/**
 * NCAA Tournament Brackets - Click to Select Winner Functionality
 */

(function() {
    'use strict';

    /**
     * Check if the current user is a guest (not logged in)
     * @returns {boolean} True if user is a guest
     */
    function isGuestUser() {
        const container = document.querySelector('.ncaa-brackets-container');
        return container && container.getAttribute('data-guest') === 'true';
    }

    // Wait for DOM to be ready
    document.addEventListener('DOMContentLoaded', function() {
        initBrackets();
        initStickyMenu();
        initTooltips();
        initBracketCollapse();
        initWelcomePopup();
        initTourModal();
        initHelpButton();
        initMobileSwiper();
    });

    /**
     * Initialize all brackets on the page
     */
    function initBrackets() {
        const brackets = document.querySelectorAll('.bracket');

        brackets.forEach(function(bracket) {
            // Load saved state first
            loadBracketState(bracket);

            // Apply result indicators if results exist
            applyResultIndicators(bracket);

            // Update scoring table
            updateScoringTable(bracket);

            // Update picks counter
            updatePicksCounter(bracket);

            // Then init click handlers
            initClickHandlers(bracket);

            // Init reset button
            initResetButton(bracket);

            // Init share button
            initShareButton(bracket);

            // Apply locked state if tournament has started
            applyLockedState(bracket);
        });
    }

    /**
     * Initialize share button functionality
     */
    function initShareButton(bracket) {
        const conferenceBracket = bracket.closest('.conference-bracket');
        if (!conferenceBracket) return;

        const shareBtn = conferenceBracket.querySelector('.share-bracket-btn');
        if (!shareBtn) return;

        shareBtn.addEventListener('click', async function(e) {
            e.preventDefault();
            const shareText = this.getAttribute('data-share-text');
            const conferenceName = this.getAttribute('data-conference-name');
            const shareUrl = window.location.origin + window.location.pathname;

            const shareData = {
                title: conferenceName + ' Tournament Pick',
                text: shareText,
                url: shareUrl
            };

            // Try native Web Share API first (mobile)
            if (navigator.share) {
                try {
                    await navigator.share(shareData);
                } catch (err) {
                    // User cancelled or error - fall back to clipboard
                    if (err.name !== 'AbortError') {
                        copyToClipboard(shareText + ' ' + shareUrl, this);
                    }
                }
            } else {
                // Fall back to clipboard copy
                copyToClipboard(shareText + ' ' + shareUrl, this);
            }
        });
    }

    /**
     * Copy text to clipboard and show feedback
     */
    function copyToClipboard(text, button) {
        function showCopiedFeedback() {
            const spanEl = button.querySelector('span');
            if (spanEl) {
                const originalText = spanEl.textContent;
                spanEl.textContent = 'Copied!';
                setTimeout(function() {
                    spanEl.textContent = originalText;
                }, 2000);
            }
        }

        // Check if clipboard API is available (requires secure context)
        if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
            navigator.clipboard.writeText(text).then(function() {
                showCopiedFeedback();
            }).catch(function() {
                // Clipboard API failed, try fallback
                fallbackCopyToClipboard(text, button, showCopiedFeedback);
            });
        } else {
            // Clipboard API not available, use fallback
            fallbackCopyToClipboard(text, button, showCopiedFeedback);
        }
    }

    /**
     * Fallback copy method for browsers without clipboard API
     */
    function fallbackCopyToClipboard(text, button, callback) {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        try {
            document.execCommand('copy');
            if (callback) callback();
        } catch (err) {
            console.error('Fallback copy failed:', err);
            showNotification('Unable to copy to clipboard', 'error');
        }
        document.body.removeChild(textarea);
    }

    /**
     * Initialize the sticky menu hamburger toggle
     */
    function initStickyMenu() {
        const hamburgerBtn = document.querySelector('.hamburger-btn');
        const conferenceNav = document.querySelector('.conference-nav');


        if (!hamburgerBtn || !conferenceNav) {
            return;
        }

        hamburgerBtn.addEventListener('click', function(e) {
            e.preventDefault();
            const isExpanded = hamburgerBtn.getAttribute('aria-expanded') === 'true';
            hamburgerBtn.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
            conferenceNav.classList.toggle('is-open');
        });

        // Close menu when clicking a nav link and expand the target bracket
        const navLinks = conferenceNav.querySelectorAll('.conference-nav-link');
        navLinks.forEach(function(link) {
            link.addEventListener('click', function(e) {
                // Close the nav menu
                hamburgerBtn.setAttribute('aria-expanded', 'false');
                conferenceNav.classList.remove('is-open');

                // Get the target bracket and expand it if collapsed
                const conferenceId = link.getAttribute('data-conference-id');
                if (conferenceId) {
                    const targetBracket = document.getElementById('conference-' + conferenceId);
                    if (targetBracket && targetBracket.getAttribute('data-collapsed') === 'true') {
                        const toggleBtn = targetBracket.querySelector('.bracket-collapse-toggle');
                        targetBracket.setAttribute('data-collapsed', 'false');
                        if (toggleBtn) {
                            toggleBtn.setAttribute('aria-expanded', 'true');
                        }
                        localStorage.setItem('ncaa_bracket_collapsed_' + conferenceId, 'false');
                    }
                }
            });
        });

        // Initialize reset all picks button
        initResetAllButton();
    }

    /**
     * Initialize tooltip touch support for mobile devices
     */
    function initTooltips() {
        const tooltipTriggers = document.querySelectorAll('.tournament-rules');

        tooltipTriggers.forEach(function(trigger) {
            const icon = trigger.querySelector('.rules-icon');
            const tooltip = trigger.querySelector('.rules-tooltip');

            if (!icon || !tooltip) {
                return;
            }

            // Toggle tooltip on click/tap
            icon.addEventListener('click', function(e) {
                e.preventDefault();
                e.stopPropagation();

                // Close any other open tooltips
                document.querySelectorAll('.rules-tooltip.is-visible').forEach(function(t) {
                    if (t !== tooltip) {
                        t.classList.remove('is-visible');
                    }
                });

                tooltip.classList.toggle('is-visible');
            });
        });

        // Close tooltips when clicking outside
        document.addEventListener('click', function(e) {
            if (!e.target.closest('.tournament-rules')) {
                document.querySelectorAll('.rules-tooltip.is-visible').forEach(function(t) {
                    t.classList.remove('is-visible');
                });
            }
        });
    }

    /**
     * Initialize bracket collapse/expand functionality
     */
    function initBracketCollapse() {
        const conferenceBrackets = document.querySelectorAll('.conference-bracket');

        conferenceBrackets.forEach(function(bracket) {
            const toggleBtn = bracket.querySelector('.bracket-collapse-toggle');
            const conferenceId = bracket.getAttribute('data-conference-id');

            if (!toggleBtn) {
                return;
            }

            // Restore collapsed state from localStorage (default to collapsed)
            const storageKey = 'ncaa_bracket_collapsed_' + conferenceId;
            const isCollapsed = localStorage.getItem(storageKey) !== 'false'; // Default collapsed

            if (isCollapsed) {
                bracket.setAttribute('data-collapsed', 'true');
                toggleBtn.setAttribute('aria-expanded', 'false');
            }

            // Toggle on click
            toggleBtn.addEventListener('click', function(e) {
                e.preventDefault();
                const currentlyCollapsed = bracket.getAttribute('data-collapsed') === 'true';

                if (currentlyCollapsed) {
                    // Expand
                    bracket.setAttribute('data-collapsed', 'false');
                    toggleBtn.setAttribute('aria-expanded', 'true');
                    localStorage.setItem(storageKey, 'false');

                    // Scroll to top of bracket after expanding
                    setTimeout(function() {
                        bracket.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 50);
                } else {
                    // Collapse
                    bracket.setAttribute('data-collapsed', 'true');
                    toggleBtn.setAttribute('aria-expanded', 'false');
                    localStorage.setItem(storageKey, 'true');
                }
            });
        });

        // Initialize expand/collapse all buttons
        initExpandCollapseAll();
    }

    /**
     * Close the conference nav menu
     */
    function closeConferenceNav() {
        const hamburgerBtn = document.querySelector('.hamburger-btn');
        const conferenceNav = document.querySelector('.conference-nav');
        if (hamburgerBtn && conferenceNav) {
            hamburgerBtn.setAttribute('aria-expanded', 'false');
            conferenceNav.classList.remove('is-open');
        }
    }

    /**
     * Initialize expand/collapse all buttons
     */
    function initExpandCollapseAll() {
        const expandAllBtn = document.querySelector('.expand-all-brackets-btn');
        const collapseAllBtn = document.querySelector('.collapse-all-brackets-btn');

        if (expandAllBtn) {
            expandAllBtn.addEventListener('click', function(e) {
                e.preventDefault();
                closeConferenceNav();
                document.querySelectorAll('.conference-bracket').forEach(function(bracket) {
                    const conferenceId = bracket.getAttribute('data-conference-id');
                    const toggleBtn = bracket.querySelector('.bracket-collapse-toggle');

                    bracket.setAttribute('data-collapsed', 'false');
                    if (toggleBtn) {
                        toggleBtn.setAttribute('aria-expanded', 'true');
                    }
                    localStorage.setItem('ncaa_bracket_collapsed_' + conferenceId, 'false');
                });
            });
        }

        if (collapseAllBtn) {
            collapseAllBtn.addEventListener('click', function(e) {
                e.preventDefault();
                closeConferenceNav();
                document.querySelectorAll('.conference-bracket').forEach(function(bracket) {
                    const conferenceId = bracket.getAttribute('data-conference-id');
                    const toggleBtn = bracket.querySelector('.bracket-collapse-toggle');

                    bracket.setAttribute('data-collapsed', 'true');
                    if (toggleBtn) {
                        toggleBtn.setAttribute('aria-expanded', 'false');
                    }
                    localStorage.setItem('ncaa_bracket_collapsed_' + conferenceId, 'true');
                });
            });
        }
    }

    /**
     * Initialize the reset all picks button
     */
    function initResetAllButton() {
        const resetAllBtn = document.querySelector('.reset-all-picks-btn');

        if (!resetAllBtn) {
            return;
        }

        resetAllBtn.addEventListener('click', function(e) {
            e.preventDefault();
            closeConferenceNav();

            // Don't allow for guest users
            if (isGuestUser()) {
                return;
            }

            function performResetAll() {
                // Send AJAX request to reset all picks
                if (typeof ncaaBrackets !== 'undefined') {
                    fetch(ncaaBrackets.ajaxUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded',
                        },
                        body: new URLSearchParams({
                            action: 'reset_all_bracket_picks',
                            nonce: ncaaBrackets.nonce
                        })
                    })
                    .then(response => response.json())
                    .then(data => {
                        if (data.success) {
                            // Reset all brackets on the page visually
                            const brackets = document.querySelectorAll('.bracket');
                            brackets.forEach(function(bracket) {
                                resetBracketVisually(bracket);
                            });

                            showNotification('All picks have been reset', 'success');
                        } else {
                            console.error('Failed to reset all picks:', data);
                            showNotification('Failed to reset picks: ' + (data.data?.message || 'Unknown error'), 'error');
                        }
                    })
                    .catch(error => {
                        console.error('Error resetting all picks:', error);
                        showNotification('Error resetting picks', 'error');
                    });
                }
            }

            console.log('Reset All clicked, ncaaModals:', window.ncaaModals);
            if (typeof window.ncaaModals !== 'undefined') {
                console.log('Calling ncaaModals.show()');
                window.ncaaModals.show({
                    title: 'Reset All Brackets',
                    message: 'Are you sure you want to reset ALL your bracket picks across all conferences? This cannot be undone.',
                    confirmText: 'Reset All',
                    cancelText: 'Cancel',
                    confirmClass: 'btn-danger',
                    onConfirm: performResetAll
                });
            } else {
                console.log('ncaaModals not defined, using fallback');
                // Fallback to browser confirm
                if (confirm('Are you sure you want to reset ALL your bracket picks across all conferences? This cannot be undone.')) {
                    performResetAll();
                }
            }
        });
    }

    /**
     * Reset a bracket visually without saving (used by reset all)
     */
    function resetBracketVisually(bracket) {
        const matchups = bracket.querySelectorAll('.matchup');

        // Remove all actual winner displays from the bracket
        const actualWinnerDisplays = bracket.querySelectorAll('.actual-winner-display, .actual-winner-display-inline');
        actualWinnerDisplays.forEach(function(display) {
            display.remove();
        });

        matchups.forEach(function(matchup) {
            const round = matchup.getAttribute('data-round');
            const teamSlots = matchup.querySelectorAll('.team-slot');

            matchup.classList.remove('result-correct', 'result-incorrect');

            teamSlots.forEach(function(slot) {
                slot.classList.remove('selected', 'correctly-advanced', 'incorrectly-advanced');

                if (slot.hasAttribute('data-from-round') || slot.hasAttribute('data-reseed-position')) {
                    const fromRound = slot.getAttribute('data-from-round');
                    const fromGame = slot.getAttribute('data-from-game');

                    clearSlot(slot, parseInt(round), fromGame);

                    if (fromRound && fromGame) {
                        slot.setAttribute('data-from-round', fromRound);
                        slot.setAttribute('data-from-game', fromGame);
                    }
                }
            });
        });

        // Force browser to repaint
        bracket.style.display = 'none';
        bracket.offsetHeight;
        bracket.style.display = '';

        // Update UI
        updateScoringTable(bracket);
        updatePicksCounter(bracket);
    }

    /**
     * Initialize reset button for a bracket
     */
    function initResetButton(bracket) {
        // Reset button is now in .conference-bracket-meta, which is a sibling of .bracket-container
        const conferenceBracket = bracket.closest('.conference-bracket');
        const resetBtn = conferenceBracket ? conferenceBracket.querySelector('.reset-bracket-btn') : null;

        if (resetBtn) {
            // Check if already initialized to prevent duplicate listeners
            if (resetBtn.hasAttribute('data-initialized')) {
                return;
            }

            resetBtn.setAttribute('data-initialized', 'true');

            resetBtn.addEventListener('click', function(e) {
                e.preventDefault();
                e.stopPropagation();

                // Get conference name for the modal
                const conferenceBracket = bracket.closest('.conference-bracket');
                const conferenceTitle = conferenceBracket ? conferenceBracket.querySelector('.conference-title') : null;
                const conferenceName = conferenceTitle ? conferenceTitle.textContent : 'this conference';

                if (typeof window.ncaaModals !== 'undefined') {
                    window.ncaaModals.show({
                        title: 'Reset Bracket',
                        message: `Are you sure you want to reset your ${conferenceName} bracket? This will clear all your selections.`,
                        confirmText: 'Reset',
                        cancelText: 'Cancel',
                        confirmClass: 'btn-danger',
                        onConfirm: function() {
                            resetBracket(bracket);
                        }
                    });
                } else {
                    // Fallback to browser confirm
                    if (confirm('Are you sure you want to reset this bracket? This will clear all your selections.')) {
                        resetBracket(bracket);
                    }
                }
            });
        }
    }

    /**
     * Reset the entire bracket to its initial state
     */
    function resetBracket(bracket) {

        // Get all matchups
        const matchups = bracket.querySelectorAll('.matchup');

        // Remove all actual winner displays from the bracket
        const actualWinnerDisplays = bracket.querySelectorAll('.actual-winner-display, .actual-winner-display-inline');
        actualWinnerDisplays.forEach(function(display) {
            display.remove();
        });

        matchups.forEach(function(matchup) {
            const round = matchup.getAttribute('data-round');
            const teamSlots = matchup.querySelectorAll('.team-slot');

            // Remove result indicator classes
            const hadCorrectClass = matchup.classList.contains('result-correct');
            const hadIncorrectClass = matchup.classList.contains('result-incorrect');

            matchup.classList.remove('result-correct', 'result-incorrect');

            teamSlots.forEach(function(slot) {
                // Remove selected and result indicator classes
                slot.classList.remove('selected', 'correctly-advanced', 'incorrectly-advanced');

                // If it's a winner slot, reset it
                if (slot.hasAttribute('data-from-round') || slot.hasAttribute('data-reseed-position')) {
                    const fromRound = slot.getAttribute('data-from-round');
                    const fromGame = slot.getAttribute('data-from-game');

                    clearSlot(slot, parseInt(round), fromGame);

                    // Restore data attributes for fixed brackets
                    if (fromRound && fromGame) {
                        slot.setAttribute('data-from-round', fromRound);
                        slot.setAttribute('data-from-game', fromGame);
                    }
                }
            });
        });

        // Force browser to repaint
        bracket.style.display = 'none';
        bracket.offsetHeight; // Trigger reflow
        bracket.style.display = '';

        // Delete all picks for this bracket via dedicated reset endpoint
        deleteAllPicks(bracket);

        // Update scoring table to reset it
        updateScoringTable(bracket);

        // Update picks counter to 0
        updatePicksCounter(bracket);

        // Show notification
        showNotification('Bracket reset', 'success');
    }

    /**
     * Delete all picks for a bracket (used during reset)
     */
    function deleteAllPicks(bracket) {
        // Don't delete for guest users
        if (isGuestUser()) {
            return;
        }

        const conferenceId = bracket.getAttribute('data-conference-id');

        if (typeof ncaaBrackets !== 'undefined') {
            fetch(ncaaBrackets.ajaxUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    action: 'reset_bracket_picks',
                    nonce: ncaaBrackets.nonce,
                    conference_id: conferenceId
                })
            })
            .then(response => response.json())
            .then(data => {
                if (!data.success) {
                    console.error('Failed to reset bracket picks:', data);
                }
            })
            .catch(error => {
                console.error('Error resetting bracket picks:', error);
            });
        }
    }

    /**
     * Load saved bracket state from data attribute
     */
    function loadBracketState(bracket) {
        const savedStateJson = bracket.getAttribute('data-saved-state');

        if (!savedStateJson || savedStateJson === 'false' || savedStateJson === '' || savedStateJson === '[]') {
            return;
        }

        try {
            const savedState = JSON.parse(savedStateJson);

            if (!savedState || Object.keys(savedState).length === 0) {
                return;
            }

            // console.log('Loading saved bracket picks:', savedState);

            // Restore picks - savedState is now {round_game: teamId}
            // Sort keys by round number to ensure earlier rounds are processed first
            // This is critical because later rounds depend on winners from earlier rounds
            const sortedKeys = Object.keys(savedState).sort(function(a, b) {
                const roundA = parseInt(a.split('_')[0]);
                const roundB = parseInt(b.split('_')[0]);
                if (roundA !== roundB) return roundA - roundB;
                // Same round, sort by game number
                const gameA = parseInt(a.split('_')[1]);
                const gameB = parseInt(b.split('_')[1]);
                return gameA - gameB;
            });

            sortedKeys.forEach(function(key) {
                const parts = key.split('_');
                const round = parts[0];
                const game = parts[1];
                const winnerTeamId = savedState[key];

                const matchup = bracket.querySelector(`.matchup[data-round="${round}"][data-game="${game}"]`);
                if (!matchup) return;

                const teamSlots = matchup.querySelectorAll('.team-slot:not(.winner-slot)');

                // Find and select the winner
                teamSlots.forEach(function(slot) {
                    if (slot.getAttribute('data-team-id') == winnerTeamId) {
                        slot.classList.add('selected');

                        // Get team data and advance to next round
                        const teamId = slot.getAttribute('data-team-id');
                        const teamSeed = slot.getAttribute('data-seed');
                        const teamName = slot.querySelector('.team-name').textContent;
                        const teamLogo = slot.querySelector('.team-logo');
                        const teamLogoUrl = teamLogo ? teamLogo.src : null;

                        advanceWinner(teamId, teamSeed, teamName, teamLogoUrl, round, game, bracket);
                    }
                });
            });
        } catch (e) {
            console.error('Error loading saved bracket state:', e);
        }
    }

    /**
     * Apply result indicators to show correct/incorrect picks
     */
    function applyResultIndicators(bracket) {
        const actualResultsJson = bracket.getAttribute('data-actual-results');

        if (!actualResultsJson || actualResultsJson === 'false' || actualResultsJson === '' || actualResultsJson === '[]') {
            return; // No results available
        }

        try {
            const actualResults = JSON.parse(actualResultsJson);

            if (!actualResults || Object.keys(actualResults).length === 0) {
                return;
            }

            // console.log('Applying result indicators:', actualResults);

            // Clear existing result indicator classes before reapplying
            const allMatchups = bracket.querySelectorAll('.matchup');
            allMatchups.forEach(function(matchup) {
                matchup.classList.remove('result-correct', 'result-incorrect');
                const teamSlots = matchup.querySelectorAll('.team-slot');
                teamSlots.forEach(function(slot) {
                    slot.classList.remove('correctly-advanced', 'incorrectly-advanced');
                });
            });

            // Build a set of eliminated teams (teams that lost in actual results)
            const eliminatedTeams = new Set();

            // First pass: find all eliminated teams from actual results
            // Only consider teams eliminated in games where they were ACTUALLY scheduled to play
            // (i.e., seeded teams or teams that actually won their previous game)
            const matchups = bracket.querySelectorAll('.matchup');
            matchups.forEach(function(matchup) {
                const round = matchup.getAttribute('data-round');
                const game = matchup.getAttribute('data-game');
                const key = round + '_' + game;

                if (actualResults[key]) {
                    const actualWinnerId = actualResults[key];
                    // Find the losing team in this matchup
                    const teamSlots = matchup.querySelectorAll('.team-slot');
                    teamSlots.forEach(function(slot) {
                        const teamId = slot.getAttribute('data-team-id');
                        if (teamId && teamId != actualWinnerId) {
                            // Check if this team was actually in this game
                            // (either seeded here or actually won their source game)
                            const fromRound = slot.getAttribute('data-from-round');
                            const fromGame = slot.getAttribute('data-from-game');
                            const reseedPosition = slot.getAttribute('data-reseed-position');

                            if (!fromRound || !fromGame) {
                                if (reseedPosition) {
                                    // Reseeding slot - team was placed here via reseeding logic
                                    // They're in this game if they have a team-id populated
                                    eliminatedTeams.add(teamId);
                                } else {
                                    // Seeded team - they were definitely in this game
                                    eliminatedTeams.add(teamId);
                                }
                            } else {
                                // Winner slot - only eliminate if they actually won their source game
                                const sourceKey = fromRound + '_' + fromGame;
                                if (actualResults[sourceKey] && actualResults[sourceKey] == teamId) {
                                    // They actually won their source game, so they were in this game
                                    eliminatedTeams.add(teamId);
                                }
                            }
                        }
                    });
                }
            });

            // console.log('Eliminated teams:', Array.from(eliminatedTeams));

            // Second pass: mark individual team slots as correctly/incorrectly advanced
            // This shows whether the user correctly predicted each team would reach this round
            matchups.forEach(function(matchup) {
                const round = parseInt(matchup.getAttribute('data-round'));
                if (round <= 1) return; // Skip first round - teams are seeded, not advanced

                const teamSlots = matchup.querySelectorAll('.team-slot');
                teamSlots.forEach(function(slot) {
                    const teamId = slot.getAttribute('data-team-id');
                    const fromRound = slot.getAttribute('data-from-round');
                    const fromGame = slot.getAttribute('data-from-game');
                    const reseedPosition = slot.getAttribute('data-reseed-position');

                    if (!teamId) return;

                    // Handle reseeding slots (like Horizon League quarterfinals/semifinals)
                    if (reseedPosition) {
                        // For reseeding slots, check if team is eliminated
                        if (eliminatedTeams.has(teamId)) {
                            slot.classList.add('incorrectly-advanced');
                            // Show the actual team that should be in this position
                            showActualTeamForReseedSlot(bracket, slot, reseedPosition, round, actualResults);
                        } else {
                            // Team is still alive - check if they actually advanced to this round
                            // by checking if they won all their previous games
                            const teamActuallyAdvanced = checkTeamActuallyAdvancedToRound(bracket, teamId, round, actualResults);
                            if (teamActuallyAdvanced === true) {
                                slot.classList.add('correctly-advanced');
                            } else if (teamActuallyAdvanced === false) {
                                slot.classList.add('incorrectly-advanced');
                                // Show the actual team that should be in this position
                                showActualTeamForReseedSlot(bracket, slot, reseedPosition, round, actualResults);
                            }
                            // If teamActuallyAdvanced is null, we don't have enough results yet
                        }
                        return;
                    }

                    // Need source game info to determine if correctly/incorrectly advanced
                    if (!fromRound || !fromGame) return;

                    // Check if there's an actual result for the source game
                    const sourceKey = fromRound + '_' + fromGame;
                    if (actualResults[sourceKey]) {
                        const actualWinnerOfSourceGame = actualResults[sourceKey];
                        if (teamId == actualWinnerOfSourceGame) {
                            // Team actually won their source game - correctly advanced to THIS round
                            slot.classList.add('correctly-advanced');
                        } else {
                            // Team lost their source game but was picked to advance - incorrectly advanced
                            slot.classList.add('incorrectly-advanced');
                        }
                    } else if (eliminatedTeams.has(teamId)) {
                        // No result for source game yet, but team is already eliminated
                        // They were picked to advance here but lost in an earlier round
                        slot.classList.add('incorrectly-advanced');
                    }
                });
            });

            // Third pass: apply indicators, marking picks of eliminated teams as incorrect
            matchups.forEach(function(matchup) {
                const round = matchup.getAttribute('data-round');
                const game = matchup.getAttribute('data-game');
                const isChampionDisplay = matchup.classList.contains('champion-display');

                // For champion-display, check the championship game result (previous round)
                let key = game ? round + '_' + game : null;
                let actualResultKey = key;

                if (isChampionDisplay) {
                    // Champion display shows winner of championship game (round 3, game 1 typically)
                    const fromRound = matchup.querySelector('.team-slot')?.getAttribute('data-from-round');
                    const fromGame = matchup.querySelector('.team-slot')?.getAttribute('data-from-game');
                    if (fromRound && fromGame) {
                        actualResultKey = fromRound + '_' + fromGame;
                    }
                }

                const selectedTeam = matchup.querySelector('.team-slot.selected');

                if (!selectedTeam) {
                    return; // No pick made for this game
                }

                const pickedTeamId = selectedTeam.getAttribute('data-team-id');

                // Check if the picked team was already eliminated in a previous round
                if (pickedTeamId && eliminatedTeams.has(pickedTeamId)) {
                    // Check if there's an actual result for the round where this team was eliminated
                    // If the elimination happened before this round, mark as incorrect
                    const eliminationRound = findEliminationRound(bracket, pickedTeamId, actualResults);

                    if (eliminationRound && parseInt(eliminationRound) < parseInt(round)) {
                        // Team was eliminated before this round - incorrect pick
                        matchup.classList.add('result-incorrect');
                        matchup.classList.remove('result-correct');

                        // Show actual winner if there's a result for this game
                        if (actualResultKey && actualResults[actualResultKey]) {
                            showActualWinner(matchup, actualResults[actualResultKey]);
                        }
                        return; // Skip to next matchup
                    }
                }

                // Check if there's an actual result for this specific game
                if (actualResultKey && actualResults[actualResultKey]) {
                    const actualWinnerId = actualResults[actualResultKey];

                    if (pickedTeamId == actualWinnerId) {
                        // Correct pick - add green indicator
                        matchup.classList.add('result-correct');
                        matchup.classList.remove('result-incorrect');
                    } else {
                        // Incorrect pick - add red indicator and show actual winner
                        matchup.classList.add('result-incorrect');
                        matchup.classList.remove('result-correct');

                        // Add actual winner info
                        showActualWinner(matchup, actualWinnerId);
                    }
                }
            });
        } catch (e) {
            console.error('Error applying result indicators:', e);
        }
    }

    /**
     * Find the round where a team was eliminated
     */
    function findEliminationRound(bracket, teamId, actualResults) {
        const matchups = bracket.querySelectorAll('.matchup');

        for (let i = 0; i < matchups.length; i++) {
            const matchup = matchups[i];
            const round = matchup.getAttribute('data-round');
            const game = matchup.getAttribute('data-game');
            const key = round + '_' + game;

            if (actualResults[key]) {
                const actualWinnerId = actualResults[key];
                // Check if this team played and lost in this matchup
                const teamSlots = matchup.querySelectorAll('.team-slot');
                for (let j = 0; j < teamSlots.length; j++) {
                    const slotTeamId = teamSlots[j].getAttribute('data-team-id');
                    if (slotTeamId == teamId && actualWinnerId != teamId) {
                        return round;
                    }
                }
            }
        }
        return null;
    }

    /**
     * Check if a team actually advanced to a given round
     * Used for reseeding brackets where we need to verify advancement
     * Returns true if advanced, false if eliminated, null if not enough results yet
     */
    function checkTeamActuallyAdvancedToRound(bracket, teamId, targetRound, actualResults) {
        // Find the team's seed to determine their starting point
        const allSlots = bracket.querySelectorAll('.team-slot[data-team-id="' + teamId + '"]');
        let teamSeed = null;

        allSlots.forEach(function(slot) {
            const seed = slot.getAttribute('data-seed');
            if (seed) {
                teamSeed = parseInt(seed);
            }
        });

        if (!teamSeed) return null;

        // For Horizon League specifically:
        // - Seeds 1-9 start in Round 2
        // - Seeds 10-11 start in Round 1
        const bracketType = bracket.getAttribute('data-bracket-type');

        if (bracketType === 'reseed-11-horizon') {
            let startingRound = (teamSeed >= 10) ? 1 : 2;

            // Check all games this team played in
            for (let round = startingRound; round < targetRound; round++) {
                const matchups = bracket.querySelectorAll('.matchup[data-round="' + round + '"]');
                let foundInRound = false;
                let wonInRound = false;

                matchups.forEach(function(matchup) {
                    const game = matchup.getAttribute('data-game');
                    const key = round + '_' + game;
                    const teamSlots = matchup.querySelectorAll('.team-slot');

                    teamSlots.forEach(function(slot) {
                        if (slot.getAttribute('data-team-id') == teamId) {
                            foundInRound = true;
                            if (actualResults[key] && actualResults[key] == teamId) {
                                wonInRound = true;
                            } else if (actualResults[key] && actualResults[key] != teamId) {
                                // Lost this game
                                wonInRound = false;
                            }
                        }
                    });
                });

                if (foundInRound) {
                    if (!wonInRound) {
                        // Check if there was a result
                        const hasResult = Array.from(matchups).some(function(matchup) {
                            const game = matchup.getAttribute('data-game');
                            const key = round + '_' + game;
                            const teamSlots = matchup.querySelectorAll('.team-slot');
                            let teamInGame = false;
                            teamSlots.forEach(function(slot) {
                                if (slot.getAttribute('data-team-id') == teamId) {
                                    teamInGame = true;
                                }
                            });
                            return teamInGame && actualResults[key];
                        });

                        if (hasResult) {
                            return false; // Team lost
                        }
                        return null; // No result yet
                    }
                }
            }

            return true; // Team won all their games up to this round
        }

        return null;
    }

    /**
     * Show the actual team that should be in a reseeding slot
     * Used when user picked wrong team to advance to a reseeding round
     */
    function showActualTeamForReseedSlot(bracket, slot, reseedPosition, round, actualResults) {
        const bracketType = bracket.getAttribute('data-bracket-type');

        if (bracketType !== 'reseed-11-horizon') return;

        // Get all actual winners from Round 2 (the source round for reseeding)
        const round2Winners = [];
        const round2Matchups = bracket.querySelectorAll('.matchup[data-round="2"]');

        round2Matchups.forEach(function(matchup) {
            const game = matchup.getAttribute('data-game');
            const key = '2_' + game;
            if (actualResults[key]) {
                const winnerId = actualResults[key];
                // Find the winner's info
                const winnerSlot = matchup.querySelector('.team-slot[data-team-id="' + winnerId + '"]');
                if (winnerSlot) {
                    const seed = winnerSlot.getAttribute('data-seed');
                    const nameSpan = winnerSlot.querySelector('.team-name');
                    const seedSpan = winnerSlot.querySelector('.seed');
                    const logoImg = winnerSlot.querySelector('.team-logo');

                    round2Winners.push({
                        teamId: winnerId,
                        seed: seed ? parseInt(seed) : 99,
                        name: nameSpan ? nameSpan.textContent : 'Unknown',
                        seedText: seedSpan ? seedSpan.textContent : '',
                        logoUrl: logoImg ? logoImg.src : ''
                    });
                }
            }
        });

        // Also check Round 1 winner (10 vs 11) - they play in Round 2
        const round1Result = actualResults['1_1'];
        if (round1Result) {
            // The R1 winner plays the 1 seed in R2G1, check if they won
            const r2g1Result = actualResults['2_1'];
            if (r2g1Result && r2g1Result == round1Result) {
                // R1 winner also won R2G1
                const r1Winner = bracket.querySelector('.matchup[data-round="1"] .team-slot[data-team-id="' + round1Result + '"]');
                if (r1Winner) {
                    const seed = r1Winner.getAttribute('data-seed');
                    const nameSpan = r1Winner.querySelector('.team-name');
                    const seedSpan = r1Winner.querySelector('.seed');
                    const logoImg = r1Winner.querySelector('.team-logo');

                    // Only add if not already in the list
                    if (!round2Winners.some(w => w.teamId == round1Result)) {
                        round2Winners.push({
                            teamId: round1Result,
                            seed: seed ? parseInt(seed) : 99,
                            name: nameSpan ? nameSpan.textContent : 'Unknown',
                            seedText: seedSpan ? seedSpan.textContent : '',
                            logoUrl: logoImg ? logoImg.src : ''
                        });
                    }
                }
            }
        }

        // Sort by seed (ascending - lowest seed number = highest seed)
        round2Winners.sort((a, b) => a.seed - b.seed);

        // Determine which team should be in this position based on reseedPosition
        let actualTeam = null;

        if (round === 3) {
            // Quarterfinals - needs 5 winners to determine positions
            if (round2Winners.length >= 5) {
                if (reseedPosition === 'lowest') {
                    actualTeam = round2Winners[4]; // 5th place = lowest remaining seed
                } else if (reseedPosition === 'second-lowest') {
                    actualTeam = round2Winners[3]; // 4th place = second lowest
                }
            }
        } else if (round === 4) {
            // Semifinals
            if (reseedPosition === 'highest') {
                if (round2Winners.length >= 1) {
                    actualTeam = round2Winners[0]; // Highest seed
                }
            } else if (reseedPosition === 'second-highest') {
                if (round2Winners.length >= 2) {
                    actualTeam = round2Winners[1]; // Second highest
                }
            } else if (reseedPosition === 'third-highest') {
                if (round2Winners.length >= 3) {
                    actualTeam = round2Winners[2]; // Third highest
                }
            }
        }

        if (!actualTeam) return;

        // Remove any existing actual winner display
        const existingActual = slot.previousElementSibling;
        if (existingActual && existingActual.classList.contains('actual-winner-display-inline')) {
            existingActual.remove();
        }

        // Create actual winner display with seed, logo, and name
        const actualDiv = document.createElement('div');
        actualDiv.className = 'actual-winner-display-inline';

        // Add seed
        if (actualTeam.seedText) {
            const seedSpan = document.createElement('span');
            seedSpan.className = 'seed';
            seedSpan.textContent = actualTeam.seedText;
            actualDiv.appendChild(seedSpan);
        }

        // Add logo
        if (actualTeam.logoUrl) {
            const logoImg = document.createElement('img');
            logoImg.src = actualTeam.logoUrl;
            logoImg.alt = '';
            logoImg.className = 'team-logo';
            actualDiv.appendChild(logoImg);
        }

        // Add team name
        const nameSpan = document.createElement('span');
        nameSpan.className = 'team-name';
        nameSpan.textContent = actualTeam.name;
        actualDiv.appendChild(nameSpan);

        // Insert before the slot
        slot.parentNode.insertBefore(actualDiv, slot);
    }

    /**
     * Show the actual winner for an incorrect pick
     */
    function showActualWinner(matchup, actualWinnerId) {
        const bracket = matchup.closest('.bracket');
        const currentRound = matchup.getAttribute('data-round');
        const currentGame = matchup.getAttribute('data-game');
        const nextRound = parseInt(currentRound) + 1;

        // Find the actual winning team's info
        let actualWinnerName = 'Unknown';
        let actualWinnerSeed = '';
        let actualWinnerLogoUrl = '';

        // First check this matchup
        const teamSlots = matchup.querySelectorAll('.team-slot');
        teamSlots.forEach(function(slot) {
            if (slot.getAttribute('data-team-id') == actualWinnerId) {
                const nameSpan = slot.querySelector('.team-name');
                const seedSpan = slot.querySelector('.seed');
                const logoImg = slot.querySelector('.team-logo');
                if (nameSpan) {
                    actualWinnerName = nameSpan.textContent;
                }
                if (seedSpan) {
                    actualWinnerSeed = seedSpan.textContent;
                }
                if (logoImg) {
                    actualWinnerLogoUrl = logoImg.src;
                }
            }
        });

        // If not found in this matchup, search the entire bracket
        if (actualWinnerName === 'Unknown' && bracket) {
            const allSlots = bracket.querySelectorAll('.team-slot[data-team-id="' + actualWinnerId + '"]');
            allSlots.forEach(function(slot) {
                if (actualWinnerName === 'Unknown') {
                    const nameSpan = slot.querySelector('.team-name');
                    const seedSpan = slot.querySelector('.seed');
                    const logoImg = slot.querySelector('.team-logo');
                    if (nameSpan && nameSpan.textContent && !nameSpan.classList.contains('empty')) {
                        actualWinnerName = nameSpan.textContent;
                    }
                    if (seedSpan && !actualWinnerSeed) {
                        actualWinnerSeed = seedSpan.textContent;
                    }
                    if (logoImg && !actualWinnerLogoUrl) {
                        actualWinnerLogoUrl = logoImg.src;
                    }
                }
            });
        }

        // 1. Show actual winner underneath the incorrect matchup
        const existingMatchupDisplay = matchup.querySelector('.actual-winner-display');
        if (existingMatchupDisplay) {
            existingMatchupDisplay.remove();
        }
        const matchupActualDiv = document.createElement('div');
        matchupActualDiv.className = 'actual-winner-display';
        matchupActualDiv.innerHTML = '<strong>Winner:</strong> ' + actualWinnerName;
        matchup.appendChild(matchupActualDiv);

        // 2. Show actual winner above the incorrectly advanced team in the next round
        const nextMatchups = bracket.querySelectorAll('.matchup[data-round="' + nextRound + '"]');

        nextMatchups.forEach(function(nextMatchup) {
            // Find the slot that receives the winner from current game
            const slots = nextMatchup.querySelectorAll('.team-slot');

            slots.forEach(function(slot) {
                const fromRound = slot.getAttribute('data-from-round');
                const fromGame = slot.getAttribute('data-from-game');

                // Check if this slot receives from our incorrect matchup
                if (fromRound === currentRound && fromGame === currentGame) {
                    // Remove any existing actual winner display before this slot
                    const existingActual = slot.previousElementSibling;
                    if (existingActual && existingActual.classList.contains('actual-winner-display-inline')) {
                        existingActual.remove();
                    }

                    // Create actual winner display with seed, logo, and name
                    const actualDiv = document.createElement('div');
                    actualDiv.className = 'actual-winner-display-inline';

                    // Add seed
                    if (actualWinnerSeed) {
                        const seedSpan = document.createElement('span');
                        seedSpan.className = 'seed';
                        seedSpan.textContent = actualWinnerSeed;
                        actualDiv.appendChild(seedSpan);
                    }

                    // Add logo
                    if (actualWinnerLogoUrl) {
                        const logoImg = document.createElement('img');
                        logoImg.src = actualWinnerLogoUrl;
                        logoImg.alt = '';
                        logoImg.className = 'team-logo';
                        actualDiv.appendChild(logoImg);
                    }

                    // Add team name
                    const nameSpan = document.createElement('span');
                    nameSpan.className = 'team-name';
                    nameSpan.textContent = actualWinnerName;
                    actualDiv.appendChild(nameSpan);

                    // Insert before the slot (as a sibling, not a child)
                    slot.parentNode.insertBefore(actualDiv, slot);
                }
            });
        });
    }

    /**
     * Update the scoring table based on current results
     */
    function updateScoringTable(bracket) {
        const scoringTable = bracket.parentElement.querySelector('.bracket-scoring-table');

        if (!scoringTable) {
            return; // No scoring table in this bracket
        }

        // Get actual results
        const actualResultsJson = bracket.getAttribute('data-actual-results');

        if (!actualResultsJson || actualResultsJson === 'false' || actualResultsJson === '' || actualResultsJson === '[]') {
            return; // No results available, table stays at 0s
        }

        try {
            const actualResults = JSON.parse(actualResultsJson);

            if (!actualResults || Object.keys(actualResults).length === 0) {
                return;
            }

            // Calculate scores by round
            const scoresByRound = {};
            const matchups = bracket.querySelectorAll('.matchup');

            matchups.forEach(function(matchup) {
                const round = matchup.getAttribute('data-round');

                if (!scoresByRound[round]) {
                    scoresByRound[round] = {
                        correct: 0,
                        total: 0
                    };
                }

                // Only count matchups that have actual results
                const roundGame = round + '_' + matchup.getAttribute('data-game');
                if (actualResults[roundGame]) {
                    scoresByRound[round].total++;

                    if (matchup.classList.contains('result-correct')) {
                        scoresByRound[round].correct++;
                    }
                }
            });

            // Update table rows
            let totalCorrect = 0;
            let totalGames = 0;

            for (const round in scoresByRound) {
                const row = scoringTable.querySelector('tr[data-round="' + round + '"]');
                if (row) {
                    const correctCell = row.querySelector('.picks-correct');
                    const gamesCell = row.querySelector('.total-games');

                    if (correctCell && gamesCell) {
                        correctCell.textContent = scoresByRound[round].correct;
                        // Preserve hardcoded game count from template, use calculated value only if higher
                        const hardcodedGames = parseInt(gamesCell.textContent) || 0;
                        const calculatedGames = scoresByRound[round].total;
                        const gamesToUse = Math.max(hardcodedGames, calculatedGames);
                        gamesCell.textContent = gamesToUse;

                        totalCorrect += scoresByRound[round].correct;
                        totalGames += gamesToUse;
                    }
                }
            }

            // Update total row
            const totalRow = scoringTable.querySelector('.total-row');
            if (totalRow) {
                const totalCorrectCell = totalRow.querySelector('.picks-correct strong');
                const totalGamesCell = totalRow.querySelector('.total-games strong');

                if (totalCorrectCell && totalGamesCell) {
                    totalCorrectCell.textContent = totalCorrect;
                    // Preserve hardcoded total from template, use calculated value only if higher
                    const hardcodedTotal = parseInt(totalGamesCell.textContent) || 0;
                    totalGamesCell.textContent = Math.max(hardcodedTotal, totalGames);
                }
            }
        } catch (e) {
            console.error('Error updating scoring table:', e);
        }
    }

    /**
     * Update the picks counter in the header meta
     */
    function updatePicksCounter(bracket) {
        // Find the conference bracket container
        const conferenceBracket = bracket.closest('.conference-bracket');
        if (!conferenceBracket) {
            return;
        }

        const picksMadeEl = conferenceBracket.querySelector('.picks-made');
        if (!picksMadeEl) {
            return;
        }

        // Count all selected team slots (picks made), excluding champion-display which is just for show
        const selectedSlots = bracket.querySelectorAll('.matchup:not(.champion-display) .team-slot.selected');
        const picksMade = selectedSlots.length;
        picksMadeEl.textContent = picksMade;

        // Update status dot
        const statusDot = conferenceBracket.querySelector('.status-dot');
        const totalGames = parseInt(conferenceBracket.getAttribute('data-total-games')) || 0;
        const conferenceId = conferenceBracket.getAttribute('data-conference-id');

        let statusClass = 'status-none';
        if (picksMade === 0) {
            statusClass = 'status-none';
        } else if (picksMade >= totalGames) {
            statusClass = 'status-complete';
        } else {
            statusClass = 'status-partial';
        }

        if (statusDot) {
            statusDot.classList.remove('status-none', 'status-partial', 'status-complete');
            statusDot.classList.add(statusClass);
        }

        // Update nav picks for this conference
        const navPicksEl = document.querySelector('[data-nav-picks="' + conferenceId + '"]');
        const navStatusEl = document.querySelector('[data-nav-status="' + conferenceId + '"]');

        if (navPicksEl) {
            navPicksEl.textContent = picksMade;
        }

        if (navStatusEl) {
            navStatusEl.classList.remove('status-none', 'status-partial', 'status-complete');
            navStatusEl.classList.add(statusClass);
        }

        // Update total progress in sticky menu
        updateTotalProgress();

        // Update champion display in header
        updateChampionDisplay(bracket);
    }

    /**
     * Update the champion display in conference header
     */
    function updateChampionDisplay(bracket) {
        const conferenceBracket = bracket.closest('.conference-bracket');
        if (!conferenceBracket) {
            return;
        }

        const conferenceId = conferenceBracket.getAttribute('data-conference-id');
        const championDisplay = conferenceBracket.querySelector('.user-champion-pick');
        if (!championDisplay) {
            return;
        }

        // Find the championship matchup (last round, game 1)
        const championMatchup = bracket.querySelector('.matchup.champion-display');
        if (!championMatchup) {
            // No champion display matchup, check for last round matchup
            const allMatchups = bracket.querySelectorAll('.matchup[data-round]');
            let maxRound = 0;
            allMatchups.forEach(function(m) {
                const r = parseInt(m.getAttribute('data-round'));
                if (r > maxRound) maxRound = r;
            });

            const lastRoundMatchup = bracket.querySelector(`.matchup[data-round="${maxRound}"][data-game="1"]`);
            if (lastRoundMatchup) {
                const selectedSlot = lastRoundMatchup.querySelector('.team-slot.selected');
                if (selectedSlot) {
                    updateChampionContent(championDisplay, selectedSlot);
                } else {
                    clearChampionContent(championDisplay);
                }
            }
            return;
        }

        // Get the selected team from champion display
        const selectedSlot = championMatchup.querySelector('.team-slot.selected');
        if (selectedSlot && selectedSlot.getAttribute('data-team-id')) {
            updateChampionContent(championDisplay, selectedSlot);
        } else {
            clearChampionContent(championDisplay);
        }
    }

    /**
     * Update champion content in header
     */
    function updateChampionContent(championDisplay, selectedSlot) {
        const teamName = selectedSlot.querySelector('.team-name');
        const teamLogo = selectedSlot.querySelector('.team-logo');

        let html = '<span class="champion-label">Champ:</span>';
        if (teamLogo) {
            html += '<img src="' + teamLogo.src + '" alt="" class="team-logo" />';
        }
        if (teamName && teamName.textContent && !teamName.classList.contains('empty')) {
            html += '<span class="champion-name">' + teamName.textContent + '</span>';
        }
        championDisplay.innerHTML = html;

        // Update share button visibility and data
        updateShareButton(championDisplay, teamName ? teamName.textContent : null);
    }

    /**
     * Clear champion content in header
     */
    function clearChampionContent(championDisplay) {
        championDisplay.innerHTML = '';

        // Hide share button when champion is cleared
        updateShareButton(championDisplay, null);
    }

    /**
     * Update share button visibility and data based on champion pick
     */
    function updateShareButton(championDisplay, championName) {
        const conferenceBracket = championDisplay.closest('.conference-bracket');
        if (!conferenceBracket) return;

        const shareBtn = conferenceBracket.querySelector('.share-bracket-btn');
        if (!shareBtn) return;

        if (championName && championName.trim() !== '') {
            // Show the share button and update its data
            shareBtn.classList.remove('hidden');
            const conferenceName = shareBtn.getAttribute('data-conference-name');
            const shareText = 'I picked ' + championName + ' to win the ' + conferenceName + ' Tournament!';
            shareBtn.setAttribute('data-share-text', shareText);
        } else {
            // Hide the share button
            shareBtn.classList.add('hidden');
        }
    }

    /**
     * Update total progress in sticky menu
     * On single bracket pages, we use the server-provided totals and adjust for the current bracket
     * When results exist, shows correct picks / total results instead of picks made / total games
     */
    function updateTotalProgress() {
        const singleBracket = document.querySelector('.conference-bracket.single');
        const summaryEl = document.querySelector('.sticky-nav-summary');

        let totalPicksMade = 0;
        let totalAllGames = 0;
        let totalCorrectPicks = 0;
        let totalResults = 0;
        let hasAnyResults = false;

        if (singleBracket && summaryEl) {
            // Single bracket page: get base totals from server, then adjust for current bracket
            const serverTotalPicks = parseInt(summaryEl.getAttribute('data-total-picks')) || 0;
            const serverTotalGames = parseInt(summaryEl.getAttribute('data-total-games')) || 0;
            const conferencePicksJson = summaryEl.getAttribute('data-conference-picks');

            const conferenceId = singleBracket.getAttribute('data-conference-id');
            const bracket = singleBracket.querySelector('.bracket');

            // Get current picks count from the visible bracket
            let currentBracketPicks = 0;
            if (bracket) {
                const selectedSlots = bracket.querySelectorAll('.matchup:not(.champion-display) .team-slot.selected');
                currentBracketPicks = selectedSlots.length;

                // Check for results
                const actualResultsJson = bracket.getAttribute('data-actual-results');
                if (actualResultsJson && actualResultsJson !== 'false' && actualResultsJson !== '' && actualResultsJson !== '[]') {
                    try {
                        const actualResults = JSON.parse(actualResultsJson);
                        if (actualResults && Object.keys(actualResults).length > 0) {
                            hasAnyResults = true;
                            totalResults = Object.keys(actualResults).length;
                            // Count correct picks
                            const correctMatchups = bracket.querySelectorAll('.matchup.result-correct');
                            totalCorrectPicks = correctMatchups.length;
                        }
                    } catch (e) {}
                }
            }

            // Get the server-side count for this conference (what was saved when page loaded)
            let serverConferencePicks = 0;
            if (conferencePicksJson) {
                try {
                    const conferencePicks = JSON.parse(conferencePicksJson);
                    serverConferencePicks = conferencePicks[conferenceId] || 0;
                } catch (e) {}
            }

            // Total = server total - server's count for this conference + current count
            totalPicksMade = serverTotalPicks - serverConferencePicks + currentBracketPicks;
            totalAllGames = serverTotalGames;
        } else {
            // Home page: count all brackets on the page
            const allBrackets = document.querySelectorAll('.conference-bracket');

            allBrackets.forEach(function(conferenceBracket) {
                const bracket = conferenceBracket.querySelector('.bracket');
                if (bracket) {
                    // Exclude champion-display which is just for show, not an actual pick
                    const selectedSlots = bracket.querySelectorAll('.matchup:not(.champion-display) .team-slot.selected');
                    totalPicksMade += selectedSlots.length;

                    // Check for results
                    const actualResultsJson = bracket.getAttribute('data-actual-results');
                    if (actualResultsJson && actualResultsJson !== 'false' && actualResultsJson !== '' && actualResultsJson !== '[]') {
                        try {
                            const actualResults = JSON.parse(actualResultsJson);
                            if (actualResults && Object.keys(actualResults).length > 0) {
                                hasAnyResults = true;
                                totalResults += Object.keys(actualResults).length;
                                // Count correct picks
                                const correctMatchups = bracket.querySelectorAll('.matchup.result-correct');
                                totalCorrectPicks += correctMatchups.length;
                            }
                        } catch (e) {}
                    }
                }
                totalAllGames += parseInt(conferenceBracket.getAttribute('data-total-games')) || 0;
            });
        }

        // Update display based on whether results exist
        const totalPicksEl = document.querySelector('.total-picks-made');
        const totalGamesEl = document.querySelector('.total-all-games');
        const summaryLabelEl = document.querySelector('.summary-label');

        if (hasAnyResults && totalResults > 0) {
            // Show correct picks / total results
            if (totalPicksEl) {
                totalPicksEl.textContent = totalCorrectPicks;
            }
            if (totalGamesEl) {
                totalGamesEl.textContent = totalResults;
            }
            if (summaryLabelEl) {
                summaryLabelEl.textContent = 'Correct Picks';
            }
        } else {
            // Show picks made / total games
            if (totalPicksEl) {
                totalPicksEl.textContent = totalPicksMade;
            }
            if (summaryLabelEl) {
                summaryLabelEl.textContent = 'Total Picks';
            }
        }

        // Update overall status dot
        const overallStatusDot = document.querySelector('.summary-picks .status-dot');
        if (overallStatusDot) {
            overallStatusDot.classList.remove('status-none', 'status-partial', 'status-complete');

            if (hasAnyResults) {
                // When results exist, base status on correct picks ratio
                if (totalCorrectPicks === 0) {
                    overallStatusDot.classList.add('status-none');
                } else if (totalCorrectPicks >= totalResults) {
                    overallStatusDot.classList.add('status-complete');
                } else {
                    overallStatusDot.classList.add('status-partial');
                }
            } else {
                // No results - base on picks made
                if (totalPicksMade === 0) {
                    overallStatusDot.classList.add('status-none');
                } else if (totalPicksMade >= totalAllGames) {
                    overallStatusDot.classList.add('status-complete');
                } else {
                    overallStatusDot.classList.add('status-partial');
                }
            }
        }
    }

    /**
     * Get the lock state of a bracket
     * Returns: 'unlocked', 'preview', or 'locked'
     * Priority: explicit status > results exist > unlocked
     * Note: Start date alone no longer locks - only actual results do
     */
    function getBracketLockState(bracket) {
        const lockStatus = bracket.getAttribute('data-lock-status');

        // If explicit status is set, use it
        if (lockStatus === 'unlocked' || lockStatus === 'preview' || lockStatus === 'locked') {
            return lockStatus;
        }

        // Check if any results exist (implies tournament is underway)
        const actualResultsJson = bracket.getAttribute('data-actual-results');
        if (actualResultsJson && actualResultsJson !== 'false' && actualResultsJson !== '' && actualResultsJson !== '[]') {
            try {
                const actualResults = JSON.parse(actualResultsJson);
                if (actualResults && Object.keys(actualResults).length > 0) {
                    return 'locked';
                }
            } catch (e) {
                // Ignore parse errors
            }
        }

        return 'unlocked'; // Default to unlocked
    }

    /**
     * Check if a bracket is locked (no picks allowed)
     * Returns true for 'preview' and 'locked' states
     */
    function isBracketLocked(bracket) {
        const state = getBracketLockState(bracket);
        return state === 'preview' || state === 'locked';
    }

    /**
     * Apply locked state to bracket based on lock status
     */
    function applyLockedState(bracket) {
        const lockState = getBracketLockState(bracket);

        if (lockState === 'unlocked') {
            return; // Nothing to do, bracket is open for picks
        }

        // Add appropriate class to bracket
        bracket.classList.add('bracket-locked');
        if (lockState === 'preview') {
            bracket.classList.add('bracket-preview');
        }

        // Change cursor to not-allowed for all team slots
        const teamSlots = bracket.querySelectorAll('.team-slot:not(.winner-slot)');
        teamSlots.forEach(function(slot) {
            slot.style.cursor = 'not-allowed';
        });

        // Disable and hide reset button
        const conferenceBracket = bracket.closest('.conference-bracket');
        if (conferenceBracket) {
            const resetBtn = conferenceBracket.querySelector('.reset-bracket-btn');
            if (resetBtn) {
                resetBtn.disabled = true;
                resetBtn.style.display = 'none';
            }

        }
    }

    /**
     * Initialize click handlers for a specific bracket
     */
    function initClickHandlers(bracket) {
        // Exclude champion-display matchups - they are display only, not clickable
        const matchups = bracket.querySelectorAll('.matchup:not(.champion-display)');

        matchups.forEach(function(matchup) {
            const teamSlots = matchup.querySelectorAll('.team-slot:not(.winner-slot)');

            teamSlots.forEach(function(teamSlot) {
                // Make clickable teams have pointer cursor
                if (teamSlot.getAttribute('data-team-id')) {
                    teamSlot.style.cursor = 'pointer';

                    // Add click event listener
                    teamSlot.addEventListener('click', function() {
                        handleTeamClick(teamSlot, matchup, bracket);
                    });
                }
            });
        });
    }

    /**
     * Handle team click to select as winner
     */
    function handleTeamClick(teamSlot, matchup, bracket) {
        // Don't allow clicking on empty slots
        if (!teamSlot.getAttribute('data-team-id')) {
            return;
        }

        // Don't allow changes if bracket is locked
        const lockState = getBracketLockState(bracket);
        if (lockState === 'preview') {
            showNotification('Bracket is in preview mode - picks not yet open', 'error');
            return;
        }
        if (lockState === 'locked') {
            showNotification('Picks are locked - tournament has started', 'error');
            return;
        }

        // Check if there's already a winner selected
        const currentWinner = matchup.querySelector('.team-slot.selected');

        // If clicking the same winner, do nothing for now
        if (currentWinner === teamSlot) {
            return;
        }

        // If there's a different winner, we need to clear the old winner from ALL subsequent rounds
        if (currentWinner) {
            const currentRound = matchup.getAttribute('data-round');
            const currentGame = matchup.getAttribute('data-game');
            const eliminatedTeamId = currentWinner.getAttribute('data-team-id');

            // Clear old winner from next round
            clearWinnerFromNextRound(currentRound, currentGame, bracket);

            // Also clear the eliminated team from all subsequent rounds (cascade)
            if (eliminatedTeamId) {
                clearEliminatedTeamFromAllRounds(eliminatedTeamId, parseInt(currentRound) + 1, bracket);
            }
        }

        // Remove selected class from all teams in this matchup
        const allTeamsInMatchup = matchup.querySelectorAll('.team-slot:not(.winner-slot)');
        allTeamsInMatchup.forEach(function(slot) {
            slot.classList.remove('selected');
        });

        // Add selected class to clicked team
        teamSlot.classList.add('selected');

        // Get team data
        const teamId = teamSlot.getAttribute('data-team-id');
        const teamSeed = teamSlot.getAttribute('data-seed');
        const teamName = teamSlot.querySelector('.team-name').textContent;
        const teamLogo = teamSlot.querySelector('.team-logo');
        const teamLogoUrl = teamLogo ? teamLogo.src : null;

        // Get matchup data
        const currentRound = matchup.getAttribute('data-round');
        const currentGame = matchup.getAttribute('data-game');

        // Advance winner to next round
        advanceWinner(teamId, teamSeed, teamName, teamLogoUrl, currentRound, currentGame, bracket);

        // Save bracket state
        saveBracketState(bracket);

        // Reapply result indicators after pick change
        applyResultIndicators(bracket);

        // Update scoring table
        updateScoringTable(bracket);

        // Update picks counter
        updatePicksCounter(bracket);
    }

    /**
     * Clear winner from next round when changing selection
     */
    function clearWinnerFromNextRound(currentRound, currentGame, bracket) {
        const nextRound = parseInt(currentRound) + 1;
        const bracketType = bracket.getAttribute('data-bracket-type');

        // Special handling for Horizon League - Round 3 and 4 use fixed advancement
        if (bracketType === 'reseed-11-horizon' && (currentRound === '3' || currentRound === '4')) {
            // Only clear the specific slot that receives this winner
            const nextMatchups = bracket.querySelectorAll(`.matchup[data-round="${nextRound}"]`);
            nextMatchups.forEach(function(nextMatchup) {
                const teamSlots = nextMatchup.querySelectorAll('.team-slot');
                teamSlots.forEach(function(slot) {
                    const fromRound = slot.getAttribute('data-from-round');
                    const fromGame = slot.getAttribute('data-from-game');
                    if (fromRound === currentRound && fromGame === currentGame) {
                        clearSlot(slot, nextRound, fromGame);
                        slot.setAttribute('data-from-round', fromRound);
                        slot.setAttribute('data-from-game', fromGame);
                    }
                });
            });
            return;
        }

        // Find the next matchup that should receive this winner
        const nextMatchups = bracket.querySelectorAll(`.matchup[data-round="${nextRound}"]`);

        // Check if next round uses reseeding
        const hasReseeding = Array.from(nextMatchups).some(m => m.getAttribute('data-reseeding') === 'true');

        if (hasReseeding) {
            // For reseeding brackets, clear ALL semifinal slots since they all depend on all QF results
            nextMatchups.forEach(function(nextMatchup) {
                const teamSlots = nextMatchup.querySelectorAll('.team-slot[data-reseed-position]');

                teamSlots.forEach(function(slot) {
                    clearSlot(slot, nextRound);
                });
            });
        } else {
            // For fixed brackets, only clear the specific slot
            nextMatchups.forEach(function(nextMatchup) {
                const teamSlots = nextMatchup.querySelectorAll('.team-slot');

                teamSlots.forEach(function(slot) {
                    const fromRound = slot.getAttribute('data-from-round');
                    const fromGame = slot.getAttribute('data-from-game');

                    // Check if this slot received the winner from current game
                    if (fromRound === currentRound && fromGame === currentGame) {
                        clearSlot(slot, nextRound, fromGame);

                        // Restore the data attributes for tracking
                        slot.setAttribute('data-from-round', fromRound);
                        slot.setAttribute('data-from-game', fromGame);
                    }
                });
            });
        }
    }

    /**
     * Clear an eliminated team from all subsequent rounds
     * This handles the cascade when changing a pick eliminates a team that was picked to win later
     */
    function clearEliminatedTeamFromAllRounds(teamId, startingRound, bracket) {
        // Get max round in bracket (look for highest round number)
        const allMatchups = bracket.querySelectorAll('.matchup[data-round]');
        let maxRound = 0;
        allMatchups.forEach(function(m) {
            const r = parseInt(m.getAttribute('data-round'));
            if (r > maxRound) maxRound = r;
        });

        // Go through all rounds from startingRound onwards
        for (let round = startingRound; round <= maxRound; round++) {
            const matchups = bracket.querySelectorAll(`.matchup[data-round="${round}"]`);

            matchups.forEach(function(matchup) {
                const game = matchup.getAttribute('data-game');

                // Check all team slots in this matchup
                const teamSlots = matchup.querySelectorAll('.team-slot');
                teamSlots.forEach(function(slot) {
                    const slotTeamId = slot.getAttribute('data-team-id');

                    // If this slot contains the eliminated team, clear it
                    if (slotTeamId === teamId) {
                        // If this team was selected as winner, also clear their advancement
                        if (slot.classList.contains('selected')) {
                            slot.classList.remove('selected');
                        }

                        // Clear the slot
                        const fromRound = slot.getAttribute('data-from-round');
                        const fromGame = slot.getAttribute('data-from-game');
                        const reseedPosition = slot.getAttribute('data-reseed-position');

                        clearSlot(slot, round, game);

                        // Restore tracking attributes
                        if (fromRound) slot.setAttribute('data-from-round', fromRound);
                        if (fromGame) slot.setAttribute('data-from-game', fromGame);
                        if (reseedPosition) slot.setAttribute('data-reseed-position', reseedPosition);
                    }
                });
            });
        }
    }

    /**
     * Clear a winner slot back to its empty state
     */
    function clearSlot(slot, round, game) {
        // Clear the slot
        slot.removeAttribute('data-team-id');
        slot.setAttribute('data-seed', '');

        const seedSpan = slot.querySelector('.seed');
        const nameSpan = slot.querySelector('.team-name');

        // Remove any logo
        const existingLogo = slot.querySelector('.team-logo');
        if (existingLogo) {
            existingLogo.remove();
        }

        if (seedSpan) seedSpan.textContent = '';
        if (nameSpan) {
            // For reseeding slots, use the position label; for fixed, use placeholder
            const reseedPosition = slot.getAttribute('data-reseed-position');
            if (reseedPosition) {
                nameSpan.textContent = getReseedPositionLabel(reseedPosition);
            } else {
                nameSpan.textContent = getPlaceholderText(round, game);
            }
            nameSpan.classList.add('empty');
        }

        // Remove clickability
        slot.style.cursor = 'default';
        slot.classList.add('winner-slot');
        slot.classList.remove('selected');
    }

    /**
     * Get label for reseeding position
     */
    function getReseedPositionLabel(position) {
        switch(position) {
            case 'highest': return 'Highest Seed';
            case 'lowest': return 'Lowest Seed';
            case 'second-highest': return '2nd Highest Seed';
            case 'second-lowest': return '2nd Lowest Seed';
            case 'third-highest': return '3rd Highest Seed';
            default: return 'TBD';
        }
    }

    /**
     * Get placeholder text for winner slots
     */
    function getPlaceholderText(round, game) {
        if (round == 2) {
            return 'Winner Game ' + game;
        } else if (round == 3) {
            return 'Winner QF' + game;
        } else if (round == 4) {
            return 'Winner SF' + game;
        }
        return 'TBD';
    }

    /**
     * Advance the winning team to the next round
     */
    function advanceWinner(teamId, teamSeed, teamName, teamLogoUrl, currentRound, currentGame, bracket) {
        const nextRound = parseInt(currentRound) + 1;
        const bracketType = bracket.getAttribute('data-bracket-type');

        // Special handling for Horizon League bracket
        if (bracketType === 'reseed-11-horizon') {
            if (currentRound === '2') {
                handleHorizonLeagueReseeding(bracket);
                return;
            }
            // For Round 3 and beyond, use fixed bracket logic (not reseeding)
            // This prevents the bye teams from being cleared
            if (currentRound === '3' || currentRound === '4') {
                handleHorizonLeagueFixedAdvancement(bracket, teamId, teamSeed, teamName, teamLogoUrl, currentRound, currentGame);
                return;
            }
        }

        // Find the next matchup that should receive this winner
        const nextMatchups = bracket.querySelectorAll(`.matchup[data-round="${nextRound}"]`);

        // Check if next round uses reseeding
        const hasReseeding = Array.from(nextMatchups).some(m => m.getAttribute('data-reseeding') === 'true');

        if (hasReseeding) {
            // Handle reseeding bracket (like America East)
            handleReseedingAdvancement(bracket, currentRound, nextRound);
        } else {
            // Handle normal fixed bracket (like Big Ten)
            nextMatchups.forEach(function(nextMatchup) {
                const winnerSlots = nextMatchup.querySelectorAll('.winner-slot');

                winnerSlots.forEach(function(slot) {
                    const fromRound = slot.getAttribute('data-from-round');
                    const fromGame = slot.getAttribute('data-from-game');

                    // Check if this slot should receive the winner from current game
                    if (fromRound === currentRound && fromGame === currentGame) {
                        populateWinnerSlot(slot, teamId, teamSeed, teamName, teamLogoUrl, nextMatchup, bracket);
                    }
                });
            });
        }
    }

    /**
     * Populate a winner slot with team data
     */
    function populateWinnerSlot(slot, teamId, teamSeed, teamName, teamLogoUrl, matchup, bracket) {
        // Update the slot with winner info
        slot.setAttribute('data-team-id', teamId);
        slot.setAttribute('data-seed', teamSeed);

        const seedSpan = slot.querySelector('.seed');
        const nameSpan = slot.querySelector('.team-name');

        if (seedSpan) seedSpan.textContent = teamSeed;

        // Handle logo - remove existing logo first
        const existingLogo = slot.querySelector('.team-logo');
        if (existingLogo) {
            existingLogo.remove();
        }

        // Add logo if URL is provided
        if (teamLogoUrl) {
            const logoImg = document.createElement('img');
            logoImg.src = teamLogoUrl;
            logoImg.alt = '';
            logoImg.className = 'team-logo';
            logoImg.setAttribute('data-team-id', teamId);
            // Insert logo after seed span, before team name
            if (seedSpan && seedSpan.nextSibling) {
                slot.insertBefore(logoImg, seedSpan.nextSibling);
            } else if (nameSpan) {
                slot.insertBefore(logoImg, nameSpan);
            }
        }

        if (nameSpan) {
            nameSpan.textContent = teamName;
            nameSpan.classList.remove('empty');
        }

        // Only make clickable if NOT in a champion-display matchup
        if (!matchup.classList.contains('champion-display')) {
            // Make the slot clickable now that it has a team
            slot.style.cursor = 'pointer';
            slot.classList.remove('winner-slot');

            // Add click handler for this newly populated slot
            slot.addEventListener('click', function() {
                handleTeamClick(slot, matchup, bracket);
            });
        } else {
            // For champion-display, add selected class so result indicators work
            slot.classList.add('selected');
        }
    }

    /**
     * Handle reseeding brackets (like America East)
     * Teams are reseeded after each round based on their original seeds
     * Updates dynamically as picks are made (doesn't wait for all games)
     */
    function handleReseedingAdvancement(bracket, currentRound, nextRound) {
        // Collect all winners from the current round
        const currentRoundMatchups = bracket.querySelectorAll(`.matchup[data-round="${currentRound}"]`);
        const winners = [];

        currentRoundMatchups.forEach(function(matchup) {
            const selectedTeam = matchup.querySelector('.team-slot.selected');
            if (selectedTeam) {
                const teamLogo = selectedTeam.querySelector('.team-logo');
                winners.push({
                    teamId: selectedTeam.getAttribute('data-team-id'),
                    seed: parseInt(selectedTeam.getAttribute('data-seed')),
                    name: selectedTeam.querySelector('.team-name').textContent,
                    logoUrl: teamLogo ? teamLogo.src : null
                });
            }
        });

        // Sort winners by seed (lowest seed number = highest seed)
        winners.sort((a, b) => a.seed - b.seed);

        // Get next round matchups
        const nextMatchups = bracket.querySelectorAll(`.matchup[data-round="${nextRound}"][data-reseeding="true"]`);

        // First, clear all reseeding slots
        nextMatchups.forEach(function(matchup) {
            const slots = matchup.querySelectorAll('.team-slot[data-reseed-position]');
            slots.forEach(function(slot) {
                clearSlot(slot, nextRound);
            });
        });

        // Check if #1 and #2 seeds both won (bracket protection)
        const hasTopTwoSeeds = winners.length >= 2 &&
                               winners[0].seed === 1 &&
                               winners[1].seed === 2;

        // Then populate based on current winners
        // Only populate when we have enough winners to avoid duplicates
        nextMatchups.forEach(function(matchup) {
            const slots = matchup.querySelectorAll('.team-slot[data-reseed-position]');

            slots.forEach(function(slot) {
                const position = slot.getAttribute('data-reseed-position');
                let team = null;

                // Determine which team goes in this slot based on reseeding position
                // Only populate if we have enough distinct winners to fill the slot
                switch(position) {
                    case 'highest':
                        team = winners.length >= 1 ? winners[0] : null; // Best seed
                        break;
                    case 'lowest':
                        // Don't populate if we only have #1 and #2 seeds (bracket protection)
                        if (hasTopTwoSeeds && winners.length === 2) {
                            team = null; // Leave empty - #2 goes in other bracket
                        } else {
                            team = winners.length >= 2 ? winners[winners.length - 1] : null; // Worst seed (need 2+ to avoid duplicate)
                        }
                        break;
                    case 'second-highest':
                        // Special case: If #1 and #2 seeds both won, put #2 here to separate them
                        if (hasTopTwoSeeds) {
                            team = winners[1]; // #2 seed
                        } else {
                            team = winners.length >= 3 ? winners[1] : null; // 2nd best seed (need 3+ normally)
                        }
                        break;
                    case 'second-lowest':
                        // Don't populate if we're in the special #1/#2 case with only 2 winners
                        if (hasTopTwoSeeds && winners.length === 2) {
                            team = null; // Leave empty until we have more winners
                        } else {
                            team = winners.length >= 4 ? winners[winners.length - 2] : null; // 2nd worst seed (need all 4)
                        }
                        break;
                }

                if (team) {
                    populateWinnerSlot(slot, team.teamId, team.seed.toString(), team.name, team.logoUrl, matchup, bracket);
                }
            });
        });
    }

    /**
     * Handle Horizon League special reseeding after Round 2
     *
     * After Round 2 (5 winners):
     * - 2 lowest seeds → Round 3 (Quarterfinals play-in)
     * - Highest seed → Bye to Round 4 Game 1
     * - 2nd & 3rd highest seeds → Bye to Round 4 Game 2
     */
    function handleHorizonLeagueReseeding(bracket) {
        // Collect all winners from Round 2
        const round2Matchups = bracket.querySelectorAll('.matchup[data-round="2"]');
        const winners = [];

        round2Matchups.forEach(function(matchup) {
            const selectedTeam = matchup.querySelector('.team-slot.selected');
            if (selectedTeam) {
                const teamLogo = selectedTeam.querySelector('.team-logo');
                winners.push({
                    teamId: selectedTeam.getAttribute('data-team-id'),
                    seed: parseInt(selectedTeam.getAttribute('data-seed')),
                    name: selectedTeam.querySelector('.team-name').textContent,
                    logoUrl: teamLogo ? teamLogo.src : null
                });
            }
        });

        // Sort winners by seed (lowest seed number = highest seed)
        winners.sort((a, b) => a.seed - b.seed);

        // Clear Round 3 reseeding slots (Quarterfinals)
        const round3Matchups = bracket.querySelectorAll('.matchup[data-round="3"][data-reseeding="true"]');
        round3Matchups.forEach(function(matchup) {
            const slots = matchup.querySelectorAll('.team-slot[data-reseed-position]');
            slots.forEach(function(slot) {
                clearSlot(slot, 3);
            });
        });

        // Clear Round 4 reseeding slots (Semifinals)
        const round4Matchups = bracket.querySelectorAll('.matchup[data-round="4"][data-reseeding="true"]');
        round4Matchups.forEach(function(matchup) {
            const slots = matchup.querySelectorAll('.team-slot[data-reseed-position]');
            slots.forEach(function(slot) {
                clearSlot(slot, 4);
            });
        });

        // Need at least 2 winners to populate Round 3
        if (winners.length >= 2) {
            // Round 3 (Quarterfinals): 2 lowest seeds play each other
            const round3Game1 = bracket.querySelector('.matchup[data-round="3"][data-game="1"]');
            if (round3Game1) {
                const secondLowestSlot = round3Game1.querySelector('.team-slot[data-reseed-position="second-lowest"]');
                const lowestSlot = round3Game1.querySelector('.team-slot[data-reseed-position="lowest"]');

                // Second lowest seed (4th best of 5)
                if (secondLowestSlot && winners.length >= 4) {
                    const team = winners[winners.length - 2];
                    populateWinnerSlot(secondLowestSlot, team.teamId, team.seed.toString(), team.name, team.logoUrl, round3Game1, bracket);
                }

                // Lowest seed (5th best of 5 = worst)
                if (lowestSlot && winners.length >= 2) {
                    const team = winners[winners.length - 1];
                    populateWinnerSlot(lowestSlot, team.teamId, team.seed.toString(), team.name, team.logoUrl, round3Game1, bracket);
                }
            }
        }

        // Round 4 (Semifinals): Top 3 seeds get byes
        // Game 1: Highest seed vs winner of QF (handled by normal advancement from Round 3)
        // Game 2: 2nd highest vs 3rd highest
        if (winners.length >= 1) {
            // Populate highest seed into Round 4 Game 1
            const round4Game1 = bracket.querySelector('.matchup[data-round="4"][data-game="1"]');
            if (round4Game1) {
                const highestSlot = round4Game1.querySelector('.team-slot[data-reseed-position="highest"]');
                if (highestSlot) {
                    const team = winners[0];
                    populateWinnerSlot(highestSlot, team.teamId, team.seed.toString(), team.name, team.logoUrl, round4Game1, bracket);
                }
            }
        }

        if (winners.length >= 3) {
            // Populate 2nd and 3rd highest into Round 4 Game 2
            const round4Game2 = bracket.querySelector('.matchup[data-round="4"][data-game="2"]');
            if (round4Game2) {
                const secondHighestSlot = round4Game2.querySelector('.team-slot[data-reseed-position="second-highest"]');
                const thirdHighestSlot = round4Game2.querySelector('.team-slot[data-reseed-position="third-highest"]');

                if (secondHighestSlot) {
                    const team = winners[1];
                    populateWinnerSlot(secondHighestSlot, team.teamId, team.seed.toString(), team.name, team.logoUrl, round4Game2, bracket);
                }

                if (thirdHighestSlot) {
                    const team = winners[2];
                    populateWinnerSlot(thirdHighestSlot, team.teamId, team.seed.toString(), team.name, team.logoUrl, round4Game2, bracket);
                }
            }
        }
    }

    /**
     * Handle fixed advancement for Horizon League (Round 3 onwards)
     * Uses data-from-round/data-from-game attributes without clearing reseeded bye teams
     */
    function handleHorizonLeagueFixedAdvancement(bracket, teamId, teamSeed, teamName, teamLogoUrl, currentRound, currentGame) {
        const nextRound = parseInt(currentRound) + 1;
        const nextMatchups = bracket.querySelectorAll(`.matchup[data-round="${nextRound}"]`);

        nextMatchups.forEach(function(nextMatchup) {
            const winnerSlots = nextMatchup.querySelectorAll('.winner-slot');

            winnerSlots.forEach(function(slot) {
                const fromRound = slot.getAttribute('data-from-round');
                const fromGame = slot.getAttribute('data-from-game');

                // Check if this slot should receive the winner from current game
                if (fromRound === currentRound && fromGame === currentGame) {
                    populateWinnerSlot(slot, teamId, teamSeed, teamName, teamLogoUrl, nextMatchup, bracket);
                }
            });
        });
    }

    /**
     * Save the current bracket state
     * @param {Element} bracket - The bracket element
     * @param {boolean} silent - If true, don't show success notification
     */
    function saveBracketState(bracket, silent) {
        // Don't save for guest users
        if (isGuestUser()) {
            return;
        }

        silent = silent || false;
        const conferenceId = bracket.getAttribute('data-conference-id');
        const bracketType = bracket.getAttribute('data-bracket-type');

        // Collect all matchup data
        const bracketData = {
            conferenceId: conferenceId,
            bracketType: bracketType,
            version: '2.0', // Track bracket structure version
            matchups: []
        };

        const matchups = bracket.querySelectorAll('.matchup');
        matchups.forEach(function(matchup) {
            const round = matchup.getAttribute('data-round');
            const game = matchup.getAttribute('data-game');
            const teams = [];
            let winner = null;

            const teamSlots = matchup.querySelectorAll('.team-slot');
            teamSlots.forEach(function(slot, index) {
                const teamId = slot.getAttribute('data-team-id') || null;
                const teamName = slot.querySelector('.team-name') ? slot.querySelector('.team-name').textContent : null;

                teams.push({
                    position: index,
                    teamId: teamId,
                    seed: slot.getAttribute('data-seed') || null,
                    teamName: teamName
                });

                // Check if this team is marked as the winner
                if (slot.classList.contains('selected')) {
                    winner = teamId;
                }
            });

            bracketData.matchups.push({
                round: round,
                game: game,
                teams: teams,
                winner: winner
            });
        });

        // console.log('Bracket state:', bracketData);

        // Send to server via AJAX
        if (typeof ncaaBrackets !== 'undefined') {
            // console.log('Sending to:', ncaaBrackets.ajaxUrl);
            fetch(ncaaBrackets.ajaxUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    action: 'save_bracket_state',
                    nonce: ncaaBrackets.nonce,
                    bracket_data: JSON.stringify(bracketData)
                })
            })
            .then(response => {
                // console.log('Response status:', response.status);
                return response.text();
            })
            .then(text => {
                // console.log('Response text:', text);
                try {
                    const data = JSON.parse(text);
                    if (data.success) {
                        // console.log('Bracket saved successfully:', data);
                        showNotification('Bracket saved!', 'success', false);
                    } else {
                        console.error('Failed to save bracket:', data);
                        if (!silent) {
                            showNotification('Failed to save bracket', 'error');
                        }
                    }
                } catch (e) {
                    console.error('Failed to parse JSON:', e, text);
                    if (!silent) {
                        showNotification('Error parsing response', 'error');
                    }
                }
            })
            .catch(error => {
                console.error('Error saving bracket:', error);
                if (!silent) {
                    showNotification('Error saving bracket', 'error');
                }
            });
        } else {
            console.error('ncaaBrackets object not found');
        }
    }

    /**
     * Show notification to user
     */
    function showNotification(message, type, srOnly) {
        srOnly = srOnly || false;

        // Create notification element
        const notification = document.createElement('div');
        notification.className = 'bracket-notification bracket-notification-' + type;

        // Add screen-reader-only class if specified
        if (srOnly) {
            notification.classList.add('sr-only');
        }

        notification.textContent = message;

        // Only add inline styles if not screen-reader-only
        if (!srOnly) {
            notification.style.cssText = `
                position: fixed;
                top: 40px;
                right: 20px;
                padding: 5px 12px;
                font-size: 12px;
                background: ${type === 'success' ? '#198754' : '#f44336'};
                color: white;
                z-index: 10000;
                animation: slideIn 0.3s ease;
            `;
        }

        document.body.appendChild(notification);

        // Remove after 3 seconds
        setTimeout(function() {
            if (!srOnly) {
                notification.style.animation = 'slideOut 0.3s ease';
            }
            setTimeout(function() {
                notification.remove();
            }, srOnly ? 0 : 300);
        }, 3000);
    }

    // Add CSS animations
    const style = document.createElement('style');
    style.textContent = `
        @keyframes slideIn {
            from {
                transform: translateX(100%);
                opacity: 0;
            }
            to {
                transform: translateX(0);
                opacity: 1;
            }
        }
        @keyframes slideOut {
            from {
                transform: translateX(0);
                opacity: 1;
            }
            to {
                transform: translateX(100%);
                opacity: 0;
            }
        }
    `;
    document.head.appendChild(style);

    /**
     * Initialize Welcome Popup
     */
    function initWelcomePopup() {
        const popup = document.getElementById('welcome-modal');
        if (!popup) return;

        const STORAGE_KEY = 'ncaa_welcome_dismissed';

        // Check if user has dismissed the popup before
        if (localStorage.getItem(STORAGE_KEY) === 'true') {
            popup.remove();
            return;
        }

        // Show the popup
        popup.classList.add('active');
        popup.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('ncaa-modal-open');
        document.body.classList.add('ncaa-modal-open');

        const overlay = popup.querySelector('.ncaa-modal-overlay');
        const dontShowCheckbox = document.getElementById('welcome-dont-show-again');
        const tourBtn = document.getElementById('welcome-tour-btn');
        const startBtn = document.getElementById('welcome-start-btn');

        function closePopup() {
            // Save preference if checkbox is checked
            if (dontShowCheckbox && dontShowCheckbox.checked) {
                localStorage.setItem(STORAGE_KEY, 'true');
            }

            popup.classList.remove('active');
            popup.setAttribute('aria-hidden', 'true');
            document.documentElement.classList.remove('ncaa-modal-open');
            document.body.classList.remove('ncaa-modal-open');

            // Remove popup from DOM after animation
            setTimeout(function() {
                popup.remove();
            }, 300);
        }

        // Close on overlay click
        if (overlay) {
            overlay.addEventListener('click', closePopup);
        }

        // Get Started button
        if (startBtn) {
            startBtn.addEventListener('click', closePopup);
        }

        // Take a Tour button
        if (tourBtn) {
            tourBtn.addEventListener('click', function() {
                closePopup();
                // Open tour modal after welcome popup closes
                setTimeout(function() {
                    if (window.ncaaTourModal) {
                        window.ncaaTourModal.open();
                    }
                }, 350);
            });
        }

        // Close on Escape key
        document.addEventListener('keydown', function handleEscape(e) {
            if (e.key === 'Escape' && popup.classList.contains('active')) {
                closePopup();
                document.removeEventListener('keydown', handleEscape);
            }
        });
    }

    /**
     * Initialize Tour Modal
     */
    function initTourModal() {
        const modal = document.getElementById('tour-modal');
        if (!modal) return;

        const steps = modal.querySelectorAll('.ncaa-tour-step');
        const dots = modal.querySelectorAll('.ncaa-tour-dot');
        const prevBtn = document.getElementById('tour-prev-btn');
        const nextBtn = document.getElementById('tour-next-btn');
        const closeBtn = modal.querySelector('[data-modal-close]');
        const overlay = modal.querySelector('.ncaa-modal-overlay');

        let currentStep = 1;
        const totalSteps = steps.length;

        function showStep(stepNum) {
            currentStep = stepNum;

            // Update steps visibility
            steps.forEach(function(step) {
                const num = parseInt(step.getAttribute('data-step'));
                step.classList.toggle('active', num === stepNum);
            });

            // Update dots
            dots.forEach(function(dot) {
                const num = parseInt(dot.getAttribute('data-step'));
                dot.classList.toggle('active', num === stepNum);
            });

            // Update button states
            if (prevBtn) {
                prevBtn.disabled = stepNum === 1;
            }

            if (nextBtn) {
                if (stepNum === totalSteps) {
                    nextBtn.textContent = 'Finish';
                } else {
                    nextBtn.textContent = 'Next';
                }
            }
        }

        function openModal() {
            modal.classList.add('active');
            modal.setAttribute('aria-hidden', 'false');
            document.documentElement.classList.add('ncaa-modal-open');
            document.body.classList.add('ncaa-modal-open');
            showStep(1);
        }

        function closeModal() {
            modal.classList.remove('active');
            modal.setAttribute('aria-hidden', 'true');
            document.documentElement.classList.remove('ncaa-modal-open');
            document.body.classList.remove('ncaa-modal-open');
        }

        // Previous button
        if (prevBtn) {
            prevBtn.addEventListener('click', function() {
                if (currentStep > 1) {
                    showStep(currentStep - 1);
                }
            });
        }

        // Next button
        if (nextBtn) {
            nextBtn.addEventListener('click', function() {
                if (currentStep < totalSteps) {
                    showStep(currentStep + 1);
                } else {
                    closeModal();
                }
            });
        }

        // Dot navigation
        dots.forEach(function(dot) {
            dot.addEventListener('click', function() {
                const stepNum = parseInt(dot.getAttribute('data-step'));
                showStep(stepNum);
            });
        });

        // Close button
        if (closeBtn) {
            closeBtn.addEventListener('click', closeModal);
        }

        // Overlay click to close
        if (overlay) {
            overlay.addEventListener('click', closeModal);
        }

        // Escape key to close
        document.addEventListener('keydown', function(e) {
            if (e.key === 'Escape' && modal.classList.contains('active')) {
                closeModal();
            }
        });

        // Make openModal accessible from welcome popup
        window.ncaaTourModal = {
            open: openModal,
            close: closeModal
        };
    }

    /**
     * Initialize Help Button
     */
    function initHelpButton() {
        const helpBtn = document.getElementById('help-tour-btn');
        if (!helpBtn) return;

        helpBtn.addEventListener('click', function() {
            if (window.ncaaTourModal) {
                window.ncaaTourModal.open();
            }
        });
    }

    /**
     * Initialize Mobile Swiper for Brackets
     * Uses Swiper.js for precise one-round-per-swipe navigation on mobile
     */
    function initMobileSwiper() {
        const MOBILE_BREAKPOINT = 767;

        function isMobile() {
            return window.innerWidth <= MOBILE_BREAKPOINT;
        }

        // Only initialize if Swiper is available
        if (typeof Swiper === 'undefined') {
            return;
        }

        const brackets = document.querySelectorAll('.bracket');

        brackets.forEach(function(bracket, bracketIndex) {
            const rounds = bracket.querySelectorAll('.bracket-round');
            if (rounds.length < 2) return;

            let swiperInstance = null;
            const bracketContainer = bracket.closest('.bracket-container');

            function initSwiper() {
                if (!isMobile() || swiperInstance) return;

                // Add swiper classes to existing elements
                bracket.classList.add('swiper');

                // Create wrapper div for rounds
                const wrapper = document.createElement('div');
                wrapper.className = 'swiper-wrapper';

                // Move rounds into wrapper and add slide class
                rounds.forEach(function(round) {
                    round.classList.add('swiper-slide');
                    wrapper.appendChild(round);
                });

                bracket.appendChild(wrapper);

                // Initialize Swiper
                swiperInstance = new Swiper(bracket, {
                    slidesPerView: 1,
                    spaceBetween: 16,
                    autoHeight: true,
                    speed: 400,
                    threshold: 5,
                    touchRatio: 1,
                    shortSwipes: true,
                    longSwipes: true,
                    longSwipesRatio: 0.25,
                    resistanceRatio: 0.85,
                    followFinger: true,
                    watchSlidesProgress: true,
                    effect: 'slide',
                    easing: 'cubic-bezier(0.25, 0.1, 0.25, 1)'
                });
            }

            function destroySwiper() {
                if (!swiperInstance) return;

                // Destroy Swiper instance
                swiperInstance.destroy(true, true);
                swiperInstance = null;

                // Remove swiper classes and unwrap rounds
                bracket.classList.remove('swiper');
                const wrapper = bracket.querySelector('.swiper-wrapper');
                if (wrapper) {
                    rounds.forEach(function(round) {
                        round.classList.remove('swiper-slide');
                        bracket.appendChild(round);
                    });
                    wrapper.remove();
                }
            }

            function handleResize() {
                if (isMobile()) {
                    initSwiper();
                } else {
                    destroySwiper();
                }
            }

            // Initialize based on current viewport
            if (isMobile()) {
                initSwiper();
            }

            // Handle resize
            let resizeTimeout;
            window.addEventListener('resize', function() {
                clearTimeout(resizeTimeout);
                resizeTimeout = setTimeout(handleResize, 150);
            });

            // Re-init when bracket is expanded
            const conferenceBracket = bracket.closest('.conference-bracket');
            if (conferenceBracket) {
                const observer = new MutationObserver(function(mutations) {
                    mutations.forEach(function(mutation) {
                        if (mutation.type === 'attributes' && mutation.attributeName === 'data-collapsed') {
                            if (conferenceBracket.getAttribute('data-collapsed') === 'false') {
                                setTimeout(function() {
                                    if (isMobile() && swiperInstance) {
                                        swiperInstance.slideTo(0, 0);
                                        swiperInstance.update();
                                    }
                                }, 100);
                            }
                        }
                    });
                });
                observer.observe(conferenceBracket, { attributes: true });
            }
        });
    }

})();
